import React, { useState, useEffect, useCallback, useRef } from 'react';
import SEO from '../components/SEO';
import '../styles/layouts/UserPage.scss';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { searchSRA, getUserAiCredits } from '../services/sraSearchService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/common/Toast';

// Import custom hooks
import { useSearchForm } from '../hooks/useSearchForm';
import { useFilterOptions } from '../hooks/useFilterOptions';
import { useLayoutEffect } from '../hooks/useLayoutEffect';
import { useSearchState } from '../contexts/SearchStateContext';

// Import components
import AppSidebar from '../components/common/AppSidebar';
import SearchForm from '../components/user/SearchForm';
import SearchResultsContainer from '../components/user/SearchResultsContainer';

// Import shared deduplication utility
import { deduplicateResults } from '../utils/deduplicationUtils';

const UserPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { 
    searchState, 
    isStateValid, 
    markSearchPerformed, 
    saveSearchState,
    saveAiSearchState,
    shouldAutoExecuteAiSearch,
    markAutoExecutionComplete
  } = useSearchState();
  const { user } = useAuth();
  const { showToast, ToastContainer } = useToast();
  
  // Use ref to track if we've restored the search to prevent infinite loops
  const restoredRef = useRef(false);
  const aiSearchRef = useRef(false);
  const lastAiQueryRef = useRef(null); // Track the last AI query to prevent duplicates
  const urlParamsProcessedRef = useRef(false); // Track if URL params have been processed
  
  // Check if this is an AI search from homepage
  const isAiSearch = searchParams.get('ai') === 'true';
  const aiQuery = searchParams.get('q') || localStorage.getItem('zahoot_ai_search_query');
  
  // Check if we're coming back from lawyer profile page with saved state
  const hasValidSavedState = isStateValid();
  console.log('UserPage initializing with valid saved state:', hasValidSavedState, 
    hasValidSavedState ? {
      criteria: searchState.searchCriteria,
      shouldPerformSearch: searchState.shouldPerformSearch,
      totalResults: searchState.totalResults,
      fuzzySearchEnabled: searchState.fuzzySearchEnabled,
      fuzzySearchCriteria: searchState.fuzzySearchCriteria
    } : 'No saved state');
  
  // Track mobile view state
  const [isMobileView, setIsMobileView] = useState(false);
  
  // Get dropdown options
  const { serviceOptions, areaOptions, budgetOptions } = useFilterOptions();
  
  // Reference to the selected form values (from the SearchForm component)
  const [formValues, setFormValues] = useState({
    service: null,
    area: null,
    budget: null
  });
  
  // Setup search form state and handlers
  const {
    animationStarted,
    contentAnimating,
    shiftLeftActive,
    searchResults,
    isLoading,
    totalResults,
    visibleResults,
    hasUserInput,
    setHasUserInput,
    handleGlobalSearch,
    handleLoadMore,
    setSearchResults,
    setTotalResults,
    setVisibleResults,
    setShiftLeftActive,
    fuzzySearchEnabled,
    toggleFuzzySearch,
    fuzzySearchCriteria,
    setFuzzySearchCriteria,
    handleFuzzySearch,
    setIsLoading
  } = useSearchForm(isMobileView, hasValidSavedState);
  
  // Apply layout effects (header hiding, scroll behavior)
  useLayoutEffect(shiftLeftActive, isMobileView);

  // Add ref for abort controller
  const abortControllerRef = useRef(null);

  // Fallback search function for when AI search fails
  const performFallbackSearch = useCallback(async (query) => {
    try {
      console.log('Performing fallback search for query:', query);
      
      // SAFETY CHECK: Don't run fallback search if AI search is still active in URL
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.get('ai') === 'true') {
        console.log('Skipping fallback search - AI search is still active in URL');
        return;
      }
      
      // Simple keyword-based search as fallback
      // Extract potential location and service from query
      const words = query.toLowerCase().split(/\s+/);
      const commonLocations = ['london', 'manchester', 'birmingham', 'leeds', 'glasgow', 'edinburgh', 'cardiff', 'bristol', 'liverpool', 'sheffield'];
      const commonServices = ['divorce', 'property', 'criminal', 'immigration', 'employment', 'personal injury', 'family', 'conveyancing'];
      
      let detectedLocation = '';
      let detectedService = '';
      
      // Try to detect location and service from query
      words.forEach(word => {
        if (commonLocations.includes(word)) {
          detectedLocation = word;
        }
        if (commonServices.includes(word)) {
          detectedService = word;
        }
      });
      
      // Create service and area objects for the standard search
      const serviceOption = detectedService ? { 
        label: detectedService.charAt(0).toUpperCase() + detectedService.slice(1),
        value: detectedService 
      } : null;
      
      const areaOption = detectedLocation ? { 
        label: detectedLocation.charAt(0).toUpperCase() + detectedLocation.slice(1),
        value: detectedLocation 
      } : null;
      
      // Use the standard search function
      await handleGlobalSearch(serviceOption, areaOption, null);
      
      // Update URL to remove AI search parameters and add detected ones
      const params = new URLSearchParams(window.location.search);
      params.delete('ai');
      params.delete('q');
      
      if (detectedService) params.set('area', detectedService);
      if (detectedLocation) params.set('city', detectedLocation);
      
      const newRelativePathQuery = window.location.pathname + '?' + params.toString();
      window.history.pushState(null, '', newRelativePathQuery);
      
    } catch (fallbackError) {
      console.error('Fallback search also failed:', fallbackError);
      // If even fallback fails, show empty results
      setSearchResults([]);
      setVisibleResults([]);
      setTotalResults(0);
    }
  }, [handleGlobalSearch, setSearchResults, setVisibleResults, setTotalResults]);

  // Handle AI search
  const handleAISearch = useCallback(async (query, isPreservedSearch = false) => {
    if (!query || aiSearchRef.current) return;
    
    console.log('Starting AI search with query:', query);
    
    // Double-check authentication before proceeding (credits will be checked server-side)
    if (!user) {
      console.log('User not authenticated in handleAISearch');
      return;
    }
    
    // Cancel previous search if running
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    
    aiSearchRef.current = true;
    
    // Clear any existing results immediately to prevent conflicts
    setSearchResults([]);
    setVisibleResults([]);
    setTotalResults(0);
    
    setIsLoading(true);
    setShiftLeftActive(true);
    setHasUserInput(true);

    try {
      // First get AI search results with abort signal
      const searchParams = { 
        q: query
      };
      
      // Add preservedSearch flag if this is a preserved search
      if (isPreservedSearch) {
        searchParams.preservedSearch = 'true';
      }
      
      const result = await searchSRA(searchParams, signal);

      // Check if request was aborted
      if (signal.aborted) {
        console.log('AI search was cancelled');
        return;
      }

      console.log('AI Search raw result:', result);

      // Update credits if provided in response
      if (result.aiCredits) {
        console.log('Updating credits from AI search response:', result.aiCredits);
        // Broadcast updated credits to other components
        window.dispatchEvent(new CustomEvent('aiCreditsUpdated', { 
          detail: result.aiCredits 
        }));
      }

      if (result && result.results && Array.isArray(result.results)) {
        console.log(`AI Search found ${result.results.length} raw results`);

        // Process AI search results - server already deduplicated
        const formattedResults = result.results.map((item, index) => {
          // Use enriched data if available, fallback to basic data with proper null handling
          const rating = parseFloat(item.rating) || parseFloat(item.google_rating) || null;
          const reviewsCount = parseInt(item.total_reviews) || parseInt(item.reviews_count) || null;
          const googleReviews = parseInt(item.google_reviews) || null;
          const zahootReviews = parseInt(item.zahoot_reviews) || null;
          const availableSlots = parseInt(item.available_slots) || 0;
          
          // Ensure ratings and reviews are null if 0 to prevent showing "0" 
          const finalRating = rating && rating > 0 ? rating : null;
          const finalReviewsCount = reviewsCount && reviewsCount > 0 ? reviewsCount : null;
          const finalGoogleReviews = googleReviews && googleReviews > 0 ? googleReviews : null;
          const finalZahootReviews = zahootReviews && zahootReviews > 0 ? zahootReviews : null;
          const finalGoogleRating = parseFloat(item.google_rating) > 0 ? parseFloat(item.google_rating) : null;
          
          return {
            id: item.solicitor_id || item.id || `ai-result-${index}`,
            name: item.practice_name || item.name || item.firm || 'Unknown Solicitor',
            firm: item.practice_name || item.name || item.firm || 'Unknown Firm',
            location: item.location || item.city || 'Location not provided',
            service: (() => {
              // Ensure only one practice area is shown
              let practiceArea = item.practice_areas || 'General Practice';
              if (typeof practiceArea === 'string' && practiceArea.includes(',')) {
                practiceArea = practiceArea.split(',')[0].trim();
              }
              return practiceArea || 'General Practice';
            })(),
            rating: finalRating,
            reviews: finalReviewsCount,
            
            // Enhanced review data with null handling
            total_reviews: finalReviewsCount,
            google_reviews: finalGoogleReviews,
            zahoot_reviews: finalZahootReviews,
            google_rating: finalGoogleRating,
            
            // Availability data
            available_slots: availableSlots,
            has_availability: availableSlots > 0,
            
            // Contact and office info
            phone: item.phone,
            email: item.email,
            website: item.website,
            address: item.address,
            postcode: item.postcode,
            offices: item.offices || [],
            
            // Practice areas detailed
            practice_areas_detailed: item.practice_areas_detailed || [],
            
            // Organization details
            orgId: item.id, // Server enrichment now provides organisation.id
            is_verified: Boolean(item.is_verified),
            is_claimed: Boolean(item.is_claimed),
            claim_status: item.claim_status,
            
            // Additional rich data
            team_photo: item.team_photo,
            overview: item.overview,
            accreditations: item.accreditations,
            recent_positive_review: item.recent_positive_review,
            
            // AI search specific
            ai_relevance_score: item.score || item.similarity_score || 0,
            isAiResult: true,
            isEnriched: Boolean(item.isEnriched),
            needsReviewFetch: true, // Flag to trigger review fetching in LawyerCard
            hasGooglePlacesData: Boolean(item.hasGooglePlacesData), // Flag to indicate Google data was fetched
            google_review_count: finalGoogleReviews, // Ensure this is passed through
            photo: null
          };
        }).filter(result => result.name && result.name !== 'Unknown Solicitor');

        console.log(`Formatted ${formattedResults.length} AI results for display`);
        
        // No client-side deduplication needed - server handles it
        setSearchResults(formattedResults);
        setVisibleResults(formattedResults.slice(0, 6));
        setTotalResults(formattedResults.length);
        
        // Save AI search state for back navigation and refresh
        saveAiSearchState(query, query);
        
        // Also save to general search state for compatibility
        saveSearchState({
          searchType: 'ai',
          searchCriteria: { aiQuery: query },
          aiQuery: query,
          shouldPerformSearch: false,
          totalResults: formattedResults.length,
          isAiSearch: true
        });
      } else {
        console.warn('AI Search returned no results or invalid format:', result);
        // Fallback to regular search when AI returns no results
        console.log('Falling back to regular search...');
        await performFallbackSearch(query);
      }
    } catch (error) {
      // Check if error is due to abort
      if (error.name === 'AbortError') {
        console.log('AI search request was cancelled');
        return;
      }
      
      console.error('Error performing AI search:', error);
      
      // Handle authentication and credit errors
      if (error.message === 'AUTHENTICATION_REQUIRED') {
        console.log('Authentication required for AI search, redirecting to login...');
        const returnUrl = `/search?ai=true&q=${encodeURIComponent(query)}`;
        navigate(`/signin?returnTo=${encodeURIComponent(returnUrl)}`);
        return;
      }
      
      if (error.message === 'CREDITS_EXHAUSTED') {
        console.log('AI credits exhausted, falling back to regular search...');
        await performFallbackSearch(query);
        return;
      }
      
      // Check if it's an OpenAI quota error or other AI-specific error
      const isAiQuotaError = error.message && (
        error.message.includes('quota') || 
        error.message.includes('429') ||
        error.message.includes('billing') ||
        error.message.includes('InsufficientQuotaError')
      );
      
      if (isAiQuotaError) {
        console.log('AI search quota exceeded, falling back to regular search...');
        // Show user-friendly message about AI search being temporarily unavailable
        console.warn('AI search temporarily unavailable due to quota limits, using regular search');
        
        // Fallback to regular search
        await performFallbackSearch(query);
      } else {
        // Other errors - still try fallback but show generic error
        console.log('AI search failed with error, attempting fallback...');
        await performFallbackSearch(query);
      }
    } finally {
      setIsLoading(false);
      aiSearchRef.current = false;
      abortControllerRef.current = null;
    }
  }, [setIsLoading, setSearchResults, setShiftLeftActive, setTotalResults, setVisibleResults, setHasUserInput, saveSearchState, user, performFallbackSearch]);



  // Handle direct URL parameters (area, city, q) for direct search access
  useEffect(() => {
    const handleDirectUrlSearch = async () => {
      // Skip if already processed URL params
      if (urlParamsProcessedRef.current) {
        return;
      }
      
      // Skip if we already have a valid saved state (user navigated back)
      if (hasValidSavedState) {
        return;
      }
      
      // Skip if this is an AI search (handled by separate effect)
      if (isAiSearch) {
        return;
      }
      
      // Skip if we have a preserved AI search that will auto-execute
      if (searchState?.searchType === 'ai' && searchState?.shouldAutoExecute) {
        console.log('Skipping URL search - preserved AI search will auto-execute');
        return;
      }
      
      // Get URL parameters
      const area = searchParams.get('area')?.trim();
      const city = searchParams.get('city')?.trim();
      const query = searchParams.get('q')?.trim();
      
      // CRITICAL FIX: If query parameter exists alongside ai=true, this is an AI search
      // Don't run standard search logic
      const hasAiParam = searchParams.get('ai') === 'true';
      if (hasAiParam && query) {
        console.log('Skipping standard search - this is an AI search with q parameter');
        return;
      }
      
      // Only proceed if we have URL parameters for standard search
      if (!area && !city && (!query || hasAiParam)) {
        return;
      }
      
      // Wait for options to be loaded
      if (serviceOptions.length === 0 || areaOptions.length === 0) {
        return;
      }
      // Mark as processed to prevent infinite loop
      urlParamsProcessedRef.current = true;
      
      // Helper function to find matching option (same as LawyerProfilePage)
      const findMatchingOption = (options, paramValue) => {
        if (!options || options.length === 0 || !paramValue) return null;

        // Decode URL encoded value and convert to lowercase for easier comparison
        const searchValue = decodeURIComponent(paramValue).toLowerCase().trim();

        // First try exact match
        let match = options.find(
          (opt) =>
            opt.label.toLowerCase() === searchValue ||
            opt.value.toLowerCase() === searchValue
        );

        // If no exact match, try partial matching
        if (!match) {
          match = options.find(
            (opt) =>
              opt.label.toLowerCase().includes(searchValue) ||
              searchValue.includes(opt.label.toLowerCase())
          );
        }

        return match || null;
      };
      
      // Map URL parameters to form values
      let matchedService = null;
      let matchedArea = null;
      
      // Map 'area' URL param to service (practice area)
      if (area) {
        matchedService = findMatchingOption(serviceOptions, area);
        
        // If no match found, create a custom option
        if (!matchedService) {
          matchedService = {
            value: decodeURIComponent(area),
            label: decodeURIComponent(area)
          };
        }
      }
      
      // Map 'city' URL param to area (location)
      if (city) {
        matchedArea = findMatchingOption(areaOptions, city);
        
        // If no match found, create a custom option
        if (!matchedArea) {
          matchedArea = {
            value: decodeURIComponent(city),
            label: decodeURIComponent(city)
          };
        }
      }
      // Update form values
      setFormValues({
        service: matchedService,
        area: matchedArea,
        budget: null
      });
      
      // Set UI state to show search form is active
      setShiftLeftActive(true);
      setHasUserInput(true);
      
      // Perform the search automatically - but double-check we're not in AI search mode
      setTimeout(() => {
        // Additional safety check to prevent race condition with AI search
        const currentAiParam = new URLSearchParams(window.location.search).get('ai');
        if (currentAiParam === 'true') {
          console.log('Aborting standard search - AI search is active');
          return;
        }
        handleGlobalSearch(matchedService, matchedArea, null);
      }, 300);
    };
    
    // Only run this effect when the component mounts or URL parameters change
    handleDirectUrlSearch();
  }, [searchParams, serviceOptions, areaOptions, hasValidSavedState, isAiSearch]);

  // Handle AI search from URL parameters
  useEffect(() => {
    const performAiSearchIfAllowed = async () => {
      // Skip if we have a preserved AI search that will auto-execute
      if (searchState?.searchType === 'ai' && searchState?.shouldAutoExecute && searchState?.aiSearchCriteria?.query) {
        console.log('Skipping URL-based AI search - preserved search will auto-execute');
        return;
      }
      
      if (isAiSearch && aiQuery && aiQuery.trim() && !aiSearchRef.current && lastAiQueryRef.current !== aiQuery) {
        console.log('Checking credentials before AI search with query:', aiQuery, {
          isFromUrl: true,
          hasPreservedState: !!searchState?.aiSearchCriteria?.query
        });
        
        // Check if user is authenticated
        if (!user) {
          console.log('User not authenticated, redirecting to signup...');
          const returnUrl = `/search?ai=true&q=${encodeURIComponent(aiQuery)}`;
          navigate(`/signup?feature=ai-search&returnTo=${encodeURIComponent(returnUrl)}`);
          return;
        }
        
        // Proceed with AI search (credits will be checked server-side)
        console.log('Executing AI search with query:', aiQuery);
        console.log('Current search results before AI search:', searchResults?.length || 0);
        lastAiQueryRef.current = aiQuery; // Store the current query
        handleAISearch(aiQuery);
      } else if (isAiSearch && !aiQuery) {
        console.log('AI search requested but no query found');
      }
    };
    
    performAiSearchIfAllowed();
  }, [isAiSearch, aiQuery, user, navigate, performFallbackSearch]); // Removed handleAISearch to prevent duplicate calls

  // Handle auto-execution of preserved AI searches (credit-aware)
  useEffect(() => {
    const handlePreservedAiSearch = async () => {
      // Only handle if we have a preserved AI search state that should auto-execute
      if (!searchState || searchState.searchType !== 'ai' || !searchState.shouldAutoExecute) {
        return;
      }

      const preservedQuery = searchState.aiSearchCriteria?.query;
      if (!preservedQuery) {
        console.log('No preserved AI query found');
        return;
      }

      console.log('Found preserved AI search query:', preservedQuery);

      // Check if user is authenticated
      if (!user) {
        console.log('User not authenticated for preserved AI search');
        const returnUrl = `/search?ai=true&q=${encodeURIComponent(preservedQuery)}`;
        navigate(`/signup?feature=ai-search&returnTo=${encodeURIComponent(returnUrl)}`);
        return;
      }

      // Check if we should auto-execute based on timing and credits
      if (shouldAutoExecuteAiSearch()) {
        console.log('Auto-executing preserved AI search without credit deduction', {
          query: preservedQuery,
          executedAt: searchState.executedAt,
          timeSince: Date.now() - searchState.executedAt
        });
        
        // Execute the search without deducting credits
        try {
          await handleAISearch(preservedQuery, true); // Pass true for isPreservedSearch
        } catch (error) {
          console.error('Error auto-executing preserved AI search:', error);
          // On error, fall back to showing the query without executing
          setIsLoading(false);
        } finally {
          markAutoExecutionComplete();
        }
      } else {
        console.log('Preserved AI search is stale, showing query without executing');
        
        // Show the query in the UI but don't execute to preserve credits
        // The search form will show the preserved query
        setIsLoading(false);
        markAutoExecutionComplete();
        
        // Optionally show a toast to inform user
        showToast('Previous AI search restored. Click search to execute again.', 'info');
      }
    };

    // Only run this once when component mounts and we have valid preserved state
    if (searchState && searchState.shouldAutoExecute && searchState.searchType === 'ai' && !isAiSearch) {
      setTimeout(() => {
        handlePreservedAiSearch();
      }, 100);
    }
  }, [searchState, user, navigate, handleAISearch, shouldAutoExecuteAiSearch, markAutoExecutionComplete, showToast, isAiSearch]);
  
  // Cleanup effect for AI search
  useEffect(() => {
    return () => {
      if (!isAiSearch) {
        aiSearchRef.current = false;
        lastAiQueryRef.current = null;
      }
    };
  }, [isAiSearch]);
  
  // Restore search state from context if coming from lawyer profile or after a refresh
  useEffect(() => {
    // Don't restore state if this is an AI search
    if (isAiSearch) {
      console.log('Skipping search state restoration - this is an AI search');
      return;
    }
    
    // Only run this once to prevent infinite loops
    if (hasValidSavedState && searchState && !restoredRef.current) {
      console.log('Restoring search state (first time)');
      restoredRef.current = true;
      
      // Check for fuzzy search state
      if (searchState.fuzzySearchEnabled) {
        console.log('Restoring fuzzy search:', searchState.fuzzySearchCriteria);
        
        // Set fuzzy search state
        toggleFuzzySearch(true);
        
        if (searchState.fuzzySearchCriteria) {
          setFuzzySearchCriteria(searchState.fuzzySearchCriteria);
        }
        
        // If search should be performed, trigger it
        if (searchState.shouldPerformSearch) {
          setTimeout(() => {
            // Additional safety check to prevent interference with AI search
            const currentParams = new URLSearchParams(window.location.search);
            if (currentParams.get('ai') === 'true') {
              console.log('Skipping fuzzy search restoration - AI search is active');
              return;
            }
            handleFuzzySearch(searchState.fuzzySearchCriteria);
            markSearchPerformed();
          }, 300);
        }
        
        return; // Exit early since we're handling fuzzy search
      }
      
      // Handle standard search if not fuzzy search
      if (searchState.searchCriteria) {
        // Restore form values from search criteria
        const savedCriteria = searchState.searchCriteria;
        
        console.log('Restoring search with criteria:', JSON.stringify(savedCriteria));
        console.log('Available service options:', serviceOptions.map(opt => ({label: opt.label, value: opt.value})));
        
        // Find matching options from the available options to ensure proper objects are used
        let matchedService = null;
        let matchedArea = null;
        let matchedBudget = null;
        
        // Handle case when service might be a string instead of an object
        if (savedCriteria?.service) {
          const serviceValue = typeof savedCriteria.service === 'string' 
            ? savedCriteria.service 
            : (savedCriteria.service.value || savedCriteria.service.label);
            
          const serviceLabel = typeof savedCriteria.service === 'string'
            ? savedCriteria.service
            : (savedCriteria.service.label || savedCriteria.service.value);
          
          console.log(`Looking for service with value/label: "${serviceValue}" or "${serviceLabel}"`);
          
          // Try to find matching service
          if (serviceOptions.length > 0) {
            matchedService = serviceOptions.find(option => 
              option.value === serviceValue || 
              option.label === serviceLabel
            );
            
            // If no match found, create a valid option object
            if (!matchedService) {
              console.log(`No match found in options, creating option object for: ${serviceLabel}`);
              matchedService = {
                value: serviceValue || serviceLabel,
                label: serviceLabel
              };
            } else {
              console.log(`Found matching service option: ${matchedService.label}`);
            }
          } else {
            // If no options available, create a valid option object
            matchedService = {
              value: serviceValue || serviceLabel,
              label: serviceLabel
            };
          }
        }
        
        // Similar approach for area
        if (savedCriteria?.area) {
          const areaValue = typeof savedCriteria.area === 'string'
            ? savedCriteria.area
            : (savedCriteria.area.value || savedCriteria.area.label);
            
          const areaLabel = typeof savedCriteria.area === 'string'
            ? savedCriteria.area
            : (savedCriteria.area.label || savedCriteria.area.value);
          
          if (areaOptions.length > 0) {
            matchedArea = areaOptions.find(option =>
              option.value === areaValue ||
              option.label === areaLabel
            );
            
            if (!matchedArea) {
              matchedArea = {
                value: areaValue || areaLabel,
                label: areaLabel
              };
            }
          } else {
            matchedArea = {
              value: areaValue || areaLabel,
              label: areaLabel
            };
          }
        }
        
        // Similar approach for budget
        if (savedCriteria?.specific) {
          const budgetValue = typeof savedCriteria.specific === 'string'
            ? savedCriteria.specific
            : (savedCriteria.specific.value || savedCriteria.specific.label);
            
          const budgetLabel = typeof savedCriteria.specific === 'string'
            ? savedCriteria.specific
            : (savedCriteria.specific.label || savedCriteria.specific.value);
          
          if (budgetOptions.length > 0) {
            matchedBudget = budgetOptions.find(option =>
              option.value === budgetValue ||
              option.label === budgetLabel
            );
            
            if (!matchedBudget) {
              matchedBudget = {
                value: budgetValue || budgetLabel,
                label: budgetLabel
              };
            }
          } else {
            matchedBudget = {
              value: budgetValue || budgetLabel,
              label: budgetLabel
            };
          }
        }
        
        console.log('Final matched values:', {
          service: matchedService ? matchedService.label : null,
          area: matchedArea ? matchedArea.label : null,
          budget: matchedBudget ? matchedBudget.label : null
        });
        
        // Restore form values with matched options
        setFormValues({
          service: matchedService,
          area: matchedArea,
          budget: matchedBudget
        });
        
        // Set UI state to show search results
        setShiftLeftActive(true);
        setHasUserInput(true);
        
        // Update the URL to reflect the search parameters
        const params = new URLSearchParams();
        if (matchedService?.label) {
          params.append("area", matchedService.label);
        }
        if (matchedArea?.label) {
          params.append("city", matchedArea.label);
        }
        if (matchedBudget?.label) {
          params.append("budget", matchedBudget.label);
        }
        
        // Update URL without reloading the page
        const newRelativePathQuery = window.location.pathname + '?' + params.toString();
        window.history.replaceState(null, '', newRelativePathQuery);
        
        // Always perform search when restoring from navigation or refresh
        if (searchState.shouldPerformSearch) {
          console.log('Auto-executing search with restored criteria');
          
          // Need a short delay to ensure form values are properly set and components are mounted
          setTimeout(() => {
            handleGlobalSearch(matchedService, matchedArea, matchedBudget);
            markSearchPerformed();
          }, 300);
        }
      }
    }
  }, [hasValidSavedState, searchState, serviceOptions, areaOptions, budgetOptions, handleGlobalSearch, markSearchPerformed, 
      setFuzzySearchCriteria, toggleFuzzySearch, handleFuzzySearch, setShiftLeftActive, setHasUserInput]);
  
  // Expose updateUserFormValues to window for SearchForm to communicate with parent
  useEffect(() => {
    window.updateUserFormValues = (values) => {
      setFormValues(values);
    };
    
    return () => {
      delete window.updateUserFormValues;
    };
  }, []);
  
  // Check for mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 1024);
    };
    
    // Initial check
    checkMobileView();
    
    // Add resize listener
    window.addEventListener('resize', checkMobileView);
    
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);
  
  // Wrap handleGlobalSearch to use form values
  const handleSearch = useCallback(() => {
    handleGlobalSearch(formValues.service, formValues.area, formValues.budget);
  }, [handleGlobalSearch, formValues]);

  return (
    <>
      <SEO 
        fallbackTitle="User Profile | Zahoot"
        fallbackDescription="View and manage your user profile on Zahoot."
      />
      <ToastContainer />
      <div className="user-page">
        {/* Sidebar Navigation */}
        <AppSidebar userType="user" />
        
        <main className="user-page__main">
          {/* Search Form */}
          <SearchForm
            serviceOptions={serviceOptions}
            areaOptions={areaOptions}
            hasUserInput={hasUserInput}
            setHasUserInput={setHasUserInput}
            handleGlobalSearch={handleSearch}
            handleFuzzySearch={handleFuzzySearch}
            handleAISearch={handleAISearch}
            animationStarted={animationStarted}
            contentAnimating={contentAnimating}
            shiftLeftActive={shiftLeftActive}
            isMobileView={isMobileView}
            initialService={formValues.service}
            initialArea={formValues.area}
            initialBudget={formValues.budget}
            fuzzySearchEnabled={fuzzySearchEnabled}
            toggleFuzzySearch={toggleFuzzySearch}
            fuzzySearchCriteria={fuzzySearchCriteria}
          />
          
          {/* Search Results */}
          {shiftLeftActive && !isAiSearch && (
            <SearchResultsContainer
              isLoading={isLoading}
              totalResults={totalResults}
              selectedService={formValues.service}
              selectedArea={formValues.area}
              handleLoadMore={handleLoadMore}
              visibleResults={visibleResults}
              searchResults={searchResults}
              isAiSearch={false}
              aiQuery=""
              searchQuery={fuzzySearchEnabled ? 
                (typeof fuzzySearchCriteria.name === 'string' ? fuzzySearchCriteria.name : '') : 
                (formValues.service?.label || formValues.area?.label ? 
                  `${formValues.service?.label || ''} ${formValues.area?.label || ''}`.trim() : '')
              }
              onRefineSearch={(newQuery) => {
                if (fuzzySearchEnabled) {
                  const newCriteria = { ...fuzzySearchCriteria, name: newQuery };
                  setFuzzySearchCriteria(newCriteria);
                  handleFuzzySearch(newCriteria);
                } else {
                  // For standard search, try to parse service/area from the query
                  const words = newQuery.toLowerCase().split(/\s+/);
                  const serviceMatch = serviceOptions.find(s => 
                    words.some(word => s.label.toLowerCase().includes(word))
                  );
                  const areaMatch = areaOptions.find(a => 
                    words.some(word => a.label.toLowerCase().includes(word))
                  );
                  handleGlobalSearch(serviceMatch, areaMatch, null);
                }
              }}
            />
          )}
          
          {/* AI Search Results - Only show when search is complete or loading */}
          {isAiSearch && (shiftLeftActive || isLoading) && (
            <SearchResultsContainer
              isLoading={isLoading}
              totalResults={totalResults}
              selectedService={formValues.service}
              selectedArea={formValues.area}
              handleLoadMore={handleLoadMore}
              visibleResults={visibleResults}
              searchResults={searchResults}
              isAiSearch={isAiSearch}
              aiQuery={aiQuery}
              searchQuery={aiQuery}
              onRefineSearch={(newQuery) => {
                handleAISearch(newQuery);
              }}
            />
          )}
        </main>
      </div>
    </>
  );
};

export default UserPage; 